import styled, { css, keyframes } from 'styled-components';
import { riseIn } from '@shared/theme/animations';
import type { MetricProps, TileProps } from './types';

export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: ${({ theme }) => theme.spacing(4)};
`;

export const SectionLabel = styled.div`
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.muted};
  margin: ${({ theme }) => theme.spacing(6)} 0 ${({ theme }) => theme.spacing(3)};
  animation: ${riseIn} 420ms ${({ theme }) => theme.motion.easeOut};
`;

export const cardEntry = keyframes`
 from { opacity: 0; transform: translateY(14px); }
 to { opacity: 1; transform: translateY(0); }
`;

export const Tile = styled.button<TileProps>`
  position: relative;
  text-align: left;
  background: ${({ theme }) => theme.colors.backgroundElevated};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  padding: ${({ theme }) => theme.spacing(5)};
  cursor: pointer;
  font: inherit;
  color: inherit;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
  min-height: 180px;
  overflow: hidden;
  animation: ${cardEntry} 480ms ${({ theme }) => theme.motion.easeOut} both;
  animation-delay: ${({ $delay }) => `${$delay}ms`};
  transition:
    transform ${({ theme }) => theme.motion.base} ${({ theme }) => theme.motion.easeOut},
    box-shadow ${({ theme }) => theme.motion.base} ${({ theme }) => theme.motion.easeOut},
    border-color ${({ theme }) => theme.motion.base} ${({ theme }) => theme.motion.easeOut};

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    padding: 1px;
    background: ${({ theme }) => theme.colors.gradient.brand};
    -webkit-mask:
      linear-gradient(#000 0 0) content-box,
      linear-gradient(#000 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    opacity: ${({ $accent }) => ($accent ? 0.2 : 0)};
    transition: opacity ${({ theme }) => theme.motion.base} ${({ theme }) => theme.motion.easeOut};
    pointer-events: none;
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${({ theme }) => theme.shadow.lg};
    border-color: transparent;
  }
  &:hover::before {
    opacity: 1;
  }

  ${({ $disabled }) =>
    $disabled &&
    css`
      cursor: not-allowed;
      opacity: 0.6;
      &:hover {
        transform: none;
        box-shadow: none;
        border-color: ${({ theme }) => theme.colors.border};
      }
      &:hover::before {
        opacity: 0;
      }
    `}
`;

export const TileArrow = styled.div`
  position: absolute;
  right: ${({ theme }) => theme.spacing(5)};
  top: ${({ theme }) => theme.spacing(5)};
  color: ${({ theme }) => theme.colors.text.muted};
  display: flex;
  align-items: center;
  transition:
    transform ${({ theme }) => theme.motion.base} ${({ theme }) => theme.motion.spring},
    color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  ${Tile}:hover & {
    transform: translateX(4px);
    color: ${({ theme }) => theme.colors.primary};
  }
`;

export const TileHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  padding-right: ${({ theme }) => theme.spacing(6)};
`;

export const TileIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.primarySoft};
  color: ${({ theme }) => theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition:
    background ${({ theme }) => theme.motion.base} ${({ theme }) => theme.motion.easeOut},
    transform ${({ theme }) => theme.motion.base} ${({ theme }) => theme.motion.spring};

  ${Tile}:hover & {
    transform: scale(1.06);
  }
`;

export const TileTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const TileNote = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-top: 2px;
`;

export const BigNumber = styled.div`
  font-size: 32px;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1;
`;

export const Metrics = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(3)} ${({ theme }) => theme.spacing(4)};
  margin-top: auto;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const Metric = styled.div<MetricProps>`
  display: flex;
  flex-direction: column;
  gap: 2px;

  & > strong {
    font-weight: 600;
    font-size: 15px;
    color: ${({ $tone, theme }) =>
      $tone === 'danger'
        ? theme.colors.status.danger
        : $tone === 'warning'
          ? theme.colors.status.warning
          : $tone === 'success'
            ? theme.colors.status.success
            : theme.colors.text.primary};
  }
`;

export const livePulseAnim = keyframes`
 0%, 100% { transform: scale(1); opacity: 0.85; }
 50% { transform: scale(1.9); opacity: 0; }
`;

export const LivePulse = styled.span`
  position: relative;
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.status.success};
  box-shadow: 0 0 6px ${({ theme }) => theme.colors.status.success}aa;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.status.success};
    animation: ${livePulseAnim} 1.4s ease-out infinite;
  }
`;

export const EmptyHint = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-top: auto;
`;

export const ErrorCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(4)};
  flex-wrap: wrap;
  background: ${({ theme }) => theme.colors.backgroundElevated};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  padding: ${({ theme }) => theme.spacing(5)};
  color: ${({ theme }) => theme.colors.text.primary};
  animation: ${riseIn} 420ms ${({ theme }) => theme.motion.easeOut};

  p {
    margin: ${({ theme }) => theme.spacing(1)} 0 0;
    font-size: 13px;
    color: ${({ theme }) => theme.colors.text.muted};
  }
`;

export const RetryButton = styled.button`
  flex-shrink: 0;
  font: inherit;
  font-weight: 600;
  cursor: pointer;
  padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(4)};
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.primarySoft};
  color: ${({ theme }) => theme.colors.primary};
  transition: opacity ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &:hover {
    opacity: 0.85;
  }
`;

// ─── Styled Components replacing inline styles ─────────────────────────

export const TileHeaderContent = styled.div`
  flex: 1;
`;

export const TileSkeletonMetrics = styled.div`
  margin-top: auto;
  display: flex;
  gap: ${({ theme }) => theme.spacing(4)};
`;

export const LivePulseTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

export const CalculatorMetricRow = styled(Metrics)`
  margin-top: ${({ theme }) => theme.spacing(2)};
`;
